/**
 * Leaderboard.tsx
 * Live leaderboard panel in the task pane (compact view).
 * Full 3D podium is in the gamification component.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Trophy } from 'lucide-react';
import { clsx } from 'clsx';
import Avatar from '@components/shared/Avatar';
import { useClassStore } from '@store/classStore';

const RANK_COLORS = ['text-yellow-400', 'text-slate-700', 'text-amber-600'];
const RANK_EMOJIS = ['🥇', '🥈', '🥉'];

export default function Leaderboard(): React.ReactElement {
  const roster = useClassStore((s) => s.roster);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center">
          <Trophy size={16} className="text-yellow-400" />
        </div>
        <div>
          <h2 className="text-sm font-bold text-slate-900">Leaderboard</h2>
          <p className="text-[10px] text-slate-500">Ranked by XP</p>
        </div>
      </div>

      {/* Top 3 podium preview */}
      {roster.length >= 3 && (
        <div className="flex items-end justify-center gap-3 pt-2 pb-3">
          {/* 2nd */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="flex flex-col items-center gap-1">
            <Avatar emoji={roster[1].avatar} name={roster[1].name} size="sm" level={roster[1].level} />
            <span className="text-xs text-slate-700 font-semibold truncate max-w-[56px] text-center">{roster[1].name}</span>
            <div className="w-14 h-10 rounded-t-lg bg-slate-400/20 border border-slate-400/30 flex items-center justify-center">
              <span className="text-lg">🥈</span>
            </div>
          </motion.div>

          {/* 1st */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="flex flex-col items-center gap-1">
            <span className="text-xs font-bold text-yellow-400">{roster[0].xp} XP</span>
            <Avatar emoji={roster[0].avatar} name={roster[0].name} size="md" level={roster[0].level} />
            <span className="text-xs text-slate-900 font-semibold truncate max-w-[64px] text-center">{roster[0].name}</span>
            <div className="w-16 h-14 rounded-t-lg bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center">
              <span className="text-2xl">🥇</span>
            </div>
          </motion.div>

          {/* 3rd */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="flex flex-col items-center gap-1">
            <Avatar emoji={roster[2].avatar} name={roster[2].name} size="sm" level={roster[2].level} />
            <span className="text-xs text-slate-700 font-semibold truncate max-w-[56px] text-center">{roster[2].name}</span>
            <div className="w-14 h-8 rounded-t-lg bg-amber-700/20 border border-amber-700/30 flex items-center justify-center">
              <span className="text-lg">🥉</span>
            </div>
          </motion.div>
        </div>
      )}

      {/* Full ranked list */}
      {roster.length === 0 ? (
        <p className="text-center text-xs text-slate-500 py-8">No students yet</p>
      ) : (
        <div className="space-y-1.5">
          {roster.map((student, i) => (
            <motion.div key={student.id} layout
              initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
              className={clsx(
                'flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-colors',
                i < 3 ? 'bg-surface-elevated border-surface-border' : 'bg-surface border-transparent',
              )}>
              <span className={clsx('text-sm font-bold w-6 text-center', RANK_COLORS[i] ?? 'text-slate-400')}>
                {i < 3 ? RANK_EMOJIS[i] : `${i + 1}`}
              </span>
              <span className="text-base">{student.avatar}</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-900 truncate">{student.name}</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1 bg-surface-border rounded-full overflow-hidden">
                    <div className="h-full bg-brand-500 rounded-full"
                      style={{ width: `${Math.min((student.xp / (roster[0]?.xp || 1)) * 100, 100)}%` }} />
                  </div>
                  <span className="text-[10px] text-slate-500 flex-shrink-0">{student.xp} XP</span>
                </div>
              </div>
              <span className="text-xs text-yellow-400">⭐{student.totalStars}</span>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
